import { useEffect } from 'react';
import { statsService } from '../services/api';

const PING_INTERVAL_MS = 20000;
// Oltre questa inattività si considera una nuova sessione (es. cliente che riapre il menu più tardi)
const SESSION_IDLE_MS = 30 * 60 * 1000;
const SESSION_KEY = 'menu_session';

const newSessionId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

/** Sessione condivisa tra le pagine del menu pubblico, rinnovata dopo 30 min di inattività */
const getSessionId = (): string | null => {
  try {
    const now = Date.now();
    const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
    const id = saved && now - saved.lastSeen < SESSION_IDLE_MS ? saved.id : newSessionId();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, lastSeen: now }));
    return id;
  } catch {
    return null;
  }
};

/**
 * Misura il tempo di permanenza sul menu pubblico: invia un heartbeat ogni 20s
 * finché la pagina è visibile, più un ultimo ping quando il cliente esce/cambia app.
 * Il server somma solo gli intervalli tra ping ravvicinati, quindi il tempo in background non conta.
 */
export const useMenuTimeTracking = (enabled = true) => {
  useEffect(() => {
    if (!enabled) return;

    const ping = (beacon = false) => {
      if (document.visibilityState !== 'visible' && !beacon) return;
      const id = getSessionId();
      if (id) statsService.pingMenu(id, beacon).catch(() => {});
    };

    ping();
    const timer = window.setInterval(() => ping(), PING_INTERVAL_MS);
    const onVisibility = () => ping(document.visibilityState === 'hidden');
    const onPageHide = () => ping(true);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [enabled]);
};
